<template>
  <button v-bind="passthroughAttrs" :type="type" :disabled="disabled" :aria-label="ariaLabel || undefined"
    :aria-busy="loading ? 'true' : undefined" :class="[
      'inline-flex items-center transition',
      'cursor-pointer disabled:cursor-not-allowed disabled:opacity-60',
      loading ? 'pointer-events-none' : '',
      'select-none outline-none',
      truncate ? 'min-w-0' : 'whitespace-nowrap',
      fullWidth ? 'w-full' : '',
      alignmentClass,
      focusClass,
      sizeClass,
      variantClasses[variant],
    ]">
    <template v-if="loading">
      <AppSpinner :size="spinnerSize" class="shrink-0" />
      <span v-if="!iconOnly">{{ loadingText }}</span>
    </template>
    <template v-else>
      <span v-if="$slots.leading" class="shrink-0 inline-flex items-center">
        <slot name="leading" />
      </span>
      <span v-if="truncate" class="min-w-0 truncate">
        <slot />
      </span>
      <slot v-else />
      <span v-if="$slots.trailing && !iconOnly" class="shrink-0 inline-flex items-center">
        <slot name="trailing" />
      </span>
    </template>
  </button>
</template>

<script>
export default {
  name: 'AppButton',
  inheritAttrs: false,

  props: {
    variant: {
      type: String,
      default: 'link',
      validator: v => ['primary', 'secondary', 'danger', 'tertiary', 'link'].includes(v),
    },
    size: {
      type: String,
      default: 'md',
      validator: v => ['xs', 'sm', 'md', 'lg', 'xl'].includes(v),
    },
    fullWidth: { type: Boolean, default: false },
    align: {
      type: String,
      default: 'center',
      validator: v => ['center', 'start', 'between'].includes(v),
    },
    type: { type: String, default: 'button' },
    loading: { type: Boolean, default: false },
    loadingText: { type: String, default: 'Submitting…' },
    disabled: { type: Boolean, default: false },
    active: { type: Boolean, default: false },
    linkTone: {
      type: String,
      default: 'default',
      validator: v => ['default', 'muted'].includes(v),
    },
    // Renders a square icon-only button — put the icon in the default slot.
    iconOnly: { type: Boolean, default: false },
    truncate: { type: Boolean, default: false },
    // Required for icon-only buttons — provides an accessible name for screen readers.
    ariaLabel: { type: String, default: '' },
  },

  computed: {
    passthroughAttrs() {
      const attrs = { ...this.$attrs };
      delete attrs.class;
      return attrs;
    },
    alignmentClass() {
      if (this.iconOnly) {
        return 'justify-center text-center';
      }

      if (this.align === 'start') {
        return 'justify-start text-left';
      }

      if (this.align === 'between') {
        return 'justify-between text-left';
      }

      return 'justify-center text-center';
    },
    isLinkVariant() {
      return this.variant === 'link';
    },
    sizeClass() {
      if (this.iconOnly) {
        return this.iconOnlySizeClasses[this.size];
      }

      if (this.isLinkVariant) {
        return this.linkSizeClasses[this.size];
      }

      return this.sizeClasses[this.size];
    },
    focusClass() {
      return 'focus-visible:ring-2 focus-visible:ring-indigo-300/60 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-900';
    },

    sizeClasses() {
      return {
        xs: 'px-2 py-1 gap-1 text-xs rounded-md',
        sm: 'px-2.5 py-1.5 gap-1 text-xs rounded-lg',
        md: 'px-3 py-1.5 gap-1.5 text-sm rounded-lg',
        lg: 'px-3 py-2 gap-1.5 text-sm rounded-xl',
        xl: 'px-4 py-2.5 gap-2 text-sm rounded-xl',
      };
    },
    linkSizeClasses() {
      return {
        xs: 'px-1 gap-1 text-xs rounded-md',
        sm: 'px-1 gap-1 text-xs rounded-lg',
        md: 'px-1 gap-1 text-sm rounded-lg',
        lg: 'px-1 gap-1 text-sm rounded-lg',
        xl: 'px-1 gap-1.5 text-sm rounded-xl',
      };
    },

    iconOnlySizeClasses() {
      return {
        xs: 'h-6 w-6 rounded-md',
        sm: 'h-8 w-8 rounded-lg',
        md: 'h-9 w-9 rounded-lg',
        lg: 'h-10 w-10 rounded-xl',
        xl: 'h-11 w-11 rounded-xl',
      };
    },
    variantClasses() {
      const linkInactiveClass = this.linkTone === 'muted'
        ? ' text-gray-500 hover:text-white'
        : ' text-gray-300 hover:text-white';

      return {
        primary:
          'font-semibold border border-indigo-400/30 bg-indigo-500/16 text-indigo-200'
          + ' hover:border-indigo-300/50 hover:bg-indigo-500/24 active:border-indigo-200/60 active:bg-indigo-500/30'
          + ' disabled:hover:border-indigo-400/30 disabled:hover:bg-indigo-500/16',
        secondary:
          'font-semibold border border-white/12 bg-white/[0.03] text-gray-200'
          + ' hover:border-white/18 hover:bg-white/[0.08] active:border-white/25 active:bg-white/[0.12]'
          + ' disabled:hover:border-white/12 disabled:hover:bg-white/[0.03]',
        danger:
          'font-semibold border border-red-500/35 bg-red-500/12 text-red-300'
          + ' hover:border-red-400/50 hover:bg-red-500/20 active:border-red-300/55 active:bg-red-500/26'
          + ' disabled:hover:border-red-500/35 disabled:hover:bg-red-500/12',
        tertiary:
          'font-semibold border border-transparent bg-transparent text-gray-300'
          + ' hover:bg-white/[0.06] hover:text-white active:bg-white/[0.1]',
        link:
          'font-semibold border border-transparent bg-transparent'
          + (this.active ? ' text-white' : linkInactiveClass)
          + ' active:text-white',
      };
    },
    spinnerSizeMap() {
      return { xs: 'sm', sm: 'sm', md: 'sm', lg: 'md', xl: 'md' };
    },
    spinnerSize() {
      return this.spinnerSizeMap[this.size] ?? 'sm';
    },
  },
};
</script>
